import { useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { CameraPtzPad } from './camera-ptz-pad'
import { CameraStreamView } from './camera-stream-view'
import { useCameraPtz } from './use-camera-ptz'
import type { ReactNode } from 'react'
import type { Camera } from './camera-types'
import type { CameraControlRequest } from '@/lib/api/cameras-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useControlCamera } from '@/hooks/use-cameras-query'
import { m } from '@/lib/paraglide/messages'

interface Props<T extends Camera> {
    /** Danh sách camera để chuyển qua lại bằng nút trước/sau. */
    cameras: T[]
    /**
     * Id camera đang xem; `null` = hộp thoại đóng. Giữ id chứ không giữ vị trí:
     * danh sách tải lại có thể đổi thứ tự hoặc mất camera, vị trí cũ khi đó trỏ
     * sang camera khác. Id không còn trong danh sách = hộp thoại đóng.
     */
    cameraId: string | null
    onCameraChange: (cameraId: string | null) => void
    /** Quyền cameras.control: xoay camera là tác động vật lý ngoài vườn. */
    canControl: boolean
    /**
     * Tiêu đề cho từng camera. Trong tab của một vùng thì tên camera là đủ;
     * màn tổng hợp cần "Hộ · Vùng · Camera" vì camera đến từ nhiều hộ.
     */
    describe?: (camera: T) => string
    /**
     * Nội dung thêm dưới bàn điều khiển, vd nút Bật/Tắt bơm khi xem camera
     * của trạm bơm — để nhân viên vừa nhìn vừa thao tác một chỗ.
     */
    extra?: ReactNode
}

/**
 * Khung xem trực tiếp MỘT camera kèm bàn điều khiển PTZ và nút chuyển nhanh
 * sang camera trước/sau trong danh sách được truyền vào.
 *
 * Dùng chung cho tab Camera của một vùng và màn tổng hợp mọi camera — khác nhau
 * chỉ ở danh sách và cách đặt tiêu đề.
 *
 * Luôn chỉ có MỘT luồng: khung hình được key theo id camera nên chuyển camera
 * là gỡ trình phát cũ (ngừng gọi luồng HLS cũ) trước khi mở luồng mới. Đóng hộp
 * thoại hay chuyển camera đều gửi lệnh dừng cho camera đang được giữ nút.
 */
export function CameraLiveDialog<T extends Camera>({
    cameras,
    cameraId: selectedId,
    onCameraChange,
    canControl,
    describe,
    extra,
}: Props<T>) {
    const index =
        selectedId === null ? -1 : cameras.findIndex((c) => c.id === selectedId)
    const camera = index === -1 ? null : cameras[index]
    const cameraId = camera?.id
    const control = useControlCamera()

    // cameraId chốt trong closure: lệnh dừng gửi khi đóng hoặc chuyển camera
    // phải tới đúng camera cũ.
    const send = useCallback(
        (command: CameraControlRequest) => {
            if (!cameraId) return Promise.resolve()
            return control.mutateAsync({ cameraId, command })
        },
        [cameraId, control],
    )

    const { holding, start, stop } = useCameraPtz(send)

    const total = cameras.length
    const go = (step: 1 | -1) => {
        if (index === -1) return
        // Dừng camera đang quay TRƯỚC khi đổi: lệnh dừng phải tới đúng camera
        // cũ, không phải camera vừa chuyển sang.
        stop()
        onCameraChange(cameras[(index + step + total) % total].id)
    }

    const handleOpenChange = (open: boolean) => {
        if (open) return
        stop()
        onCameraChange(null)
    }

    const ptzDisabled = !canControl || !camera?.can_control

    return (
        <Dialog open={Boolean(camera)} onOpenChange={handleOpenChange}>
            {/* To gấp rưỡi bản cũ (3xl → 6xl), và không bao giờ vượt khung
                nhìn: chừa lề 1rem mỗi bên, cuộn nếu màn hình quá thấp. */}
            <DialogContent className="max-h-[calc(100svh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-6xl">
                <DialogHeader>
                    <DialogTitle className="pr-8">
                        {camera
                            ? m.grower_area_camera_live_label({
                                  name: describe
                                      ? describe(camera)
                                      : camera.name,
                              })
                            : m.grower_area_camera_live_title()}
                    </DialogTitle>
                </DialogHeader>

                {camera && (
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_200px]">
                        <div className="flex min-w-0 flex-col gap-2">
                            <CameraStreamView
                                key={camera.id}
                                camera={camera}
                                active
                                mode="live"
                                showReload
                                // Giới hạn theo cả chiều cao màn hình: giữ tỉ lệ
                                // 16:9 mà laptop màn thấp vẫn không tràn.
                                className="mx-auto aspect-video w-full max-w-[calc((100svh-12rem)*16/9)] rounded-md"
                            />
                            {total > 1 && (
                                <div className="flex items-center justify-between gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        aria-label={m.grower_area_camera_prev()}
                                        onClick={() => go(-1)}
                                    >
                                        <ChevronLeft className="size-4" />
                                        <span className="hidden sm:inline">
                                            {m.grower_area_camera_prev()}
                                        </span>
                                    </Button>
                                    <span className="text-sm tabular-nums text-muted-foreground">
                                        {m.grower_area_camera_position({
                                            current: index + 1,
                                            total,
                                        })}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        aria-label={m.grower_area_camera_next()}
                                        onClick={() => go(1)}
                                    >
                                        <span className="hidden sm:inline">
                                            {m.grower_area_camera_next()}
                                        </span>
                                        <ChevronRight className="size-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {m.grower_area_camera_ptz_title()}
                            </span>
                            <CameraPtzPad
                                holding={holding}
                                onStart={start}
                                onStop={stop}
                                disabled={ptzDisabled}
                            />
                            {extra}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
